<template>
    <div class="ai-session-drawer-backdrop" @click.self="$emit('close')">
        <div class="ai-session-drawer">

            <header class="ai-session-drawer-header">
                <div>
                    <h3>
                        <i class="fa fa-magic"></i>
                        AI conversation
                    </h3>
                    <div class="ai-session-drawer-meta">
                        <span v-if="session.salesperson">{{ session.salesperson.name }}</span>
                        <span v-if="session.created_at">
                            · {{ formatDate(session.created_at) }}
                        </span>
                        <span v-if="session.model_version">· {{ session.model_version }}</span>
                        <span v-if="totalTokens > 0">· {{ formatTokens(totalTokens) }} tokens</span>
                        <span class="ai-session-drawer-status" :class="'is-' + session.status">
                            {{ session.status }}
                        </span>
                    </div>
                </div>
                <button type="button" class="ai-session-drawer-close" @click="$emit('close')" aria-label="Close">&times;</button>
            </header>

            <div class="ai-session-drawer-body">
                <div v-if="!session.messages || session.messages.length === 0" class="ai-session-drawer-empty">
                    No messages recorded for this session.
                </div>

                <div v-else>
                    <div
                        v-for="msg in renderedMessages"
                        :key="msg.key"
                        class="asd-msg"
                        :class="'asd-' + msg.kind"
                    >
                        <div v-if="msg.kind === 'user'" class="asd-bubble asd-user">
                            {{ msg.content }}
                        </div>

                        <div v-else-if="msg.kind === 'assistant'" class="asd-assistant">
                            <div class="asd-avatar"><i class="fa fa-magic"></i></div>
                            <div class="asd-assistant-content">{{ msg.content }}</div>
                        </div>

                        <div v-else-if="msg.kind === 'tool'" class="asd-tool">
                            <button
                                type="button"
                                class="asd-tool-toggle"
                                :class="{ 'is-error': msg.error }"
                                @click="toggleTool(msg.key)"
                            >
                                <i class="fa" :class="expandedTools[msg.key] ? 'fa-caret-down' : 'fa-caret-right'"></i>
                                {{ msg.tool_name }}
                                <span v-if="msg.error" class="asd-tool-err">error</span>
                            </button>
                            <div v-if="expandedTools[msg.key]" class="asd-tool-detail">
                                <div v-if="msg.tool_params"><strong>params:</strong>
                                    <pre>{{ formatJson(msg.tool_params) }}</pre>
                                </div>
                                <div v-if="msg.tool_result"><strong>result:</strong>
                                    <pre>{{ formatJson(msg.tool_result) }}</pre>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
export default {
    props: {
        session: { type: Object, required: true },
    },

    data: () => ({ expandedTools: {} }),

    computed: {
        totalTokens() {
            return (this.session.input_tokens || 0) + (this.session.output_tokens || 0);
        },

        renderedMessages() {
            const msgs = this.session.messages || [];
            const byUseId = {};
            msgs.forEach(m => {
                if (m.role === 'tool_result' && m.tool_use_id) byUseId[m.tool_use_id] = m;
            });
            const out = [];
            msgs.forEach(m => {
                if (m.role === 'tool_result') return;
                if (m.role === 'tool_call') {
                    const result = m.tool_use_id ? byUseId[m.tool_use_id] : null;
                    const error = result && result.tool_result && typeof result.tool_result === 'object' && result.tool_result.error;
                    out.push({
                        key: 'tc-' + m.id,
                        kind: 'tool',
                        tool_name: m.tool_name,
                        tool_params: m.tool_params,
                        tool_result: result ? result.tool_result : null,
                        error: !!error,
                    });
                } else if (m.role === 'assistant') {
                    out.push({ key: 'a-' + m.id, kind: 'assistant', content: m.content });
                } else if (m.role === 'user') {
                    out.push({ key: 'u-' + m.id, kind: 'user', content: m.content });
                }
            });
            return out;
        },
    },

    mounted() {
        document.addEventListener('keydown', this.onKey);
    },

    beforeDestroy() {
        document.removeEventListener('keydown', this.onKey);
    },

    methods: {
        onKey(e) {
            if (e.key === 'Escape') this.$emit('close');
        },
        toggleTool(key) {
            this.$set(this.expandedTools, key, !this.expandedTools[key]);
        },
        formatJson(v) {
            try { return JSON.stringify(v, null, 2); } catch (e) { return String(v); }
        },
        formatDate(iso) {
            const d = new Date(iso);
            return d.toLocaleString('en-GB', {
                day: 'numeric', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
        },
        formatTokens(n) {
            if (n < 1000) return n;
            return Math.round(n / 100) / 10 + 'k';
        },
    },
};
</script>

<style>
.ai-session-drawer-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, .5);
    z-index: 10001;
    display: flex;
    justify-content: flex-end;
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    color: #1f2937;
}

.ai-session-drawer {
    background: #fff;
    width: 600px;
    max-width: 100%;
    height: 100%;
    box-shadow: -10px 0 30px rgba(15, 23, 42, .2);
    display: flex;
    flex-direction: column;
}

.ai-session-drawer-header {
    padding: 16px 20px;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.ai-session-drawer-header h3 {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 6px;
}

.ai-session-drawer-header h3 .fa {
    color: #7c3aed;
}

.ai-session-drawer-meta {
    margin-top: 4px;
    font-size: 12px;
    color: #6b7280;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.ai-session-drawer-status {
    margin-left: 4px;
    padding: 1px 7px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.ai-session-drawer-status.is-active    { background: #d1fae5; color: #065f46; }
.ai-session-drawer-status.is-completed { background: #ddd6fe; color: #5b21b6; }
.ai-session-drawer-status.is-abandoned { background: #fed7aa; color: #9a3412; }

.ai-session-drawer-close {
    background: none;
    border: none;
    font-size: 26px;
    line-height: 1;
    color: #6b7280;
    cursor: pointer;
    margin-left: auto;
}

.ai-session-drawer-close:hover { color: #1f2937; }

.ai-session-drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.ai-session-drawer-empty {
    text-align: center;
    color: #6b7280;
    padding: 40px 0;
}

.asd-msg { display: flex; }

.asd-user {
    margin-left: auto;
    max-width: 80%;
    background: #eff6ff;
    border-radius: 14px 14px 4px 14px;
    padding: 8px 12px;
    font-size: 13.5px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-wrap: break-word;
}

.asd-assistant {
    display: flex;
    gap: 8px;
    max-width: 95%;
}

.asd-avatar {
    width: 24px; height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2563eb, #7c3aed);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex-shrink: 0;
}

.asd-assistant-content {
    color: #1f2937;
    font-size: 13.5px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-wrap: break-word;
}

.asd-tool { width: 100%; padding-left: 32px; }

.asd-tool-toggle {
    background: none;
    border: none;
    padding: 1px 0;
    cursor: pointer;
    color: #6b7280;
    font-size: 12px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: inherit;
}

.asd-tool-toggle:hover { color: #1f2937; }

.asd-tool-toggle.is-error { color: #b91c1c; }

.asd-tool-err {
    background: #fee2e2;
    color: #b91c1c;
    border-radius: 999px;
    padding: 0 6px;
    font-size: 10px;
    font-weight: 500;
}

.asd-tool-detail {
    margin-top: 4px;
    padding: 8px 10px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 11px;
}

.asd-tool-detail strong {
    display: inline-block;
    margin: 4px 0 2px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 10px;
    color: #6b7280;
}

.asd-tool-detail pre {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 6px 8px;
    margin: 0 0 4px;
    font-size: 11px;
    line-height: 1.4;
    max-height: 200px;
    overflow: auto;
    color: #0f172a;
}
</style>
